import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

export default function StayConnectedFooter() {
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [isError, setIsError] = useState(false);

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.xaodao.io/api';

  // Clear message after 5 seconds
  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => {
        setMessage('');
        setIsError(false);
      }, 5000);
      return () => clearTimeout(timer); // Cleanup timeout on unmount or message change
    }
  }, [message]);

  // Function to validate email format
  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email) && email.length <= 100;
  };

  // Function to check subscription status
  async function checkSubscription(email) {
    try {
      const response = await fetch(`${API_BASE_URL}/subscription/check/${encodeURIComponent(email)}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      const data = await response.json();
      if (response.ok) {
        return { success: true, subscribed: data.data.data.subscribed };
      } else {
        console.error('Check subscription error:', data);
        return { success: false, error: data };
      }
    } catch (error) {
      console.error('Network error in checkSubscription:', error);
      return { success: false, error };
    }
  }

  // Function to subscribe email
  async function subscribeEmail(email) {
    try {
      const response = await fetch(`${API_BASE_URL}/subscription`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });
      const data = await response.json();
      if (response.ok) {
        return { success: true, data };
      } else {
        console.error('Subscribe email error:', data);
        return { success: false, error: data };
      }
    } catch (error) {
      console.error('Network error in subscribeEmail:', error);
      return { success: false, error };
    }
  }

  // Handle subscribe button click
  const handleSubscribe = async () => {
    if (!email) {
      console.error('No email provided');
      return;
    }

    if (!isValidEmail(email)) {
      console.error('Invalid email format:', email);
      setMessage('Please provide a valid email address');
      setIsError(true);
      return;
    }

    // Check subscription status first
    const checkResult = await checkSubscription(email);
    if (!checkResult.success) {
      // Log error but don't show in UI unless it's a 409 (already subscribed)
      if (checkResult.error?.error?.statusCode === 409) {
        setMessage('This email is already subscribed to the newsletter');
        setIsError(true);
      }
      return;
    }

    if (checkResult.subscribed) {
      setMessage('This email is already subscribed to the newsletter');
      setIsError(true);
      return;
    }

    // If not subscribed, proceed to subscribe
    const subscribeResult = await subscribeEmail(email);
    if (subscribeResult.success) {
      setMessage('Email subscribed successfully to the newsletter');
      setIsError(false);
      setEmail(''); // Clear input on success
    } else {
      // Only show in UI if it's a 409 error (already subscribed)
      if (subscribeResult.error?.error?.statusCode === 409) {
        setMessage('This email is already subscribed to the newsletter');
        setIsError(true);
      }
    }
  };

  // Smooth scroll handler
  const handleSmoothScroll = (e) => {
    e.preventDefault();
    const targetId = e.currentTarget.getAttribute('href').substring(1);
    const targetElement = document.getElementById(targetId);
    if (targetElement) {
      targetElement.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <footer
      className="relative w-screen min-h-fit flex flex-col items-center justify-center py-10"
      style={{ backgroundImage: `url('./footerBg.png')`, backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat' }}
    >
      <div className="flex flex-col justify-center items-center w-full py-10 relative" style={{ marginTop: '-160px', zIndex: 30 }}>
        <div className="bg-[#f7f8f9] rounded-2xl shadow-xl px-6 md:px-10 py-8 w-full max-w-5xl flex flex-col items-center gap-8 overflow-hidden border border-black/10">
          {/* Decorative vectors left/right */}
          <h2 className="text-2xl font-semibold text-[#121828] mb-2 text-center">Stay connected.</h2>
          <p className="text-base text-[#121828] text-center mb-4 max-w-xl">
            Want to get updates on governance outcomes, funding rounds, or ecosystem proposals?
            <br />Drop your email. We’ll only send what matters.
          </p>
          <div className='mb-6'>
            <div className="flex flex-col md:flex-row gap-3 items-center">
              <input
                type="email"
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="px-3 py-2 rounded-lg border-black bg-white/40 text-[#414653] placeholder:text-[#414653] w-full max-w-[240px] border border-black"
              />
              <button
                onClick={handleSubscribe}
                className="bg-white text-[#121828] font-semibold px-5 py-2 rounded-lg shadow hover:bg-gray-100"
              >
                Subscribe
              </button>
            </div>
            {message && (
              <div className={`text-sm ${isError ? 'text-red-600' : 'text-green-600'} text-center mt-3`}>
                {message}
              </div>
            )}
          </div>
          <div className="flex flex-row gap-6 items-center mb-6">
            <a href={process.env.REACT_APP_LINKEDIN_URL} target="_blank" rel="noopener noreferrer">
              <img src="./linkedIn.png" alt="LinkedIn" className="w-7 h-7 cursor-pointer" />
            </a>
            <span className="bg-white w-px h-7" />
            <a href={process.env.REACT_APP_FACEBOOK_URL} target="_blank" rel="noopener noreferrer">
              <img src="./facebook.png" alt="Facebook" className="w-7 h-7 cursor-pointer" />
            </a>
            {/* <span className="bg-white w-px h-7" /> */}
            {/* <a href={process.env.REACT_APP_X_URL} target="_blank" rel="noopener noreferrer"> */}
              {/* <img src="./x.png" alt="X Logo" className="w-7 h-7 cursor-pointer" /> */}
            {/* </a> */}
          </div>
        </div>
      </div>
      <div className="max-w-6xl w-full mx-auto px-4 py-8 flex flex-col items-center gap-10">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12 w-full mb-8">
          <div className="flex flex-col items-center md:items-start">
            <h3 className="text-lg font-semibold text-white mb-4 text-center md:text-left">XAO DAO</h3>
            <ul className="text-white/70 text-base space-y-2 text-center md:text-left cursor-pointer">
              <li><a href="#about" onClick={handleSmoothScroll}>About</a></li>
              <li><Link to="/announcements">Announcements</Link></li>
              <li><a href="#join" onClick={handleSmoothScroll} >Join XAO DAO</a></li>
              <li><a href="#governance" onClick={handleSmoothScroll} >Governance Process</a></li>
              <li><a href="#proposals" onClick={handleSmoothScroll}>Proposals</a></li>
              <li><a href="#grants" onClick={handleSmoothScroll}>Grants</a></li>
            </ul>
          </div>
          <div className="flex flex-col items-center md:items-start">
            <h3 className="text-lg font-semibold text-white mb-4 text-center md:text-left">Resources</h3>
            <ul className="text-white/70 text-base space-y-2 text-center md:text-left cursor-pointer">
              <li><a href="#faq" onClick={handleSmoothScroll} className="">FAQs</a></li>
              <li><a href="mailto:membership@xaodao.io" className="">Contact Us</a></li>
            </ul>
          </div>
          <div className="flex flex-col items-center md:items-start">
            <h3 className="text-lg font-semibold text-white mb-4 text-center md:text-left">Legal</h3>
            <ul className="text-white/70 text-base space-y-2 text-center md:text-left cursor-pointer">
              <li><a href={process.env.REACT_APP_TERMS_AND_CONDITIONS_URL} target="_blank" rel="noopener noreferrer" className="">Terms & Conditions</a></li>
              <li><a href={process.env.REACT_APP_PRIVACY_POLICY_URL} target="_blank" rel="noopener noreferrer" className="">Privacy Policy</a></li>
            </ul>
          </div>
        </div>
        <div className="w-full h-px bg-white/10 mb-4" />
        <p className="text-white/50 text-sm text-center">XAO DAO © 2025 All Rights Reserved.</p>
      </div>
    </footer>
  );
}